// routes/components/PlayerProtectedRoute.tsx
/**
 * PlayerProtectedRoute - Protects player-only routes
 * Redirects non-players to landing page, unauthenticated to player registration
 */

import { ReactElement } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { PageLoading } from '../../components/common/Loading';
import { ROUTES } from '../../config/routes';
import type { PlayerProtectedRouteProps } from '../types';

export function PlayerProtectedRoute({ children }: PlayerProtectedRouteProps): ReactElement {
  const { user, loading, isPlayer, isStaff } = useAuth();

  if (loading) {
    return <PageLoading />;
  }

  // Not logged in - redirect to player registration
  if (!user) {
    return <Navigate to={ROUTES.PLAYER_REGISTER} replace />;
  }

  // Allow players, staff, admins, and store accounts to access
  // the player portal. Store owners commonly play in their own
  // events, and staff often have a Konami / Bandai ID linked to
  // their account too — bouncing them on /player/* would mean
  // they can't see their own tournament from the player side.
  const isStoreAccount = user.accountType === 'store';
  if (!isPlayer() && !isStaff() && !isStoreAccount) {
    return <Navigate to={ROUTES.LANDING} replace />;
  }

  return <>{children}</>;
}

export default PlayerProtectedRoute;
